/**
 * Variety - LLM Provider Icons
 * Maps LLM vendors to their icon paths
 */

/**
 * Provider string to icon path mapping
 * Keys must match the provider values from the API exactly (lowercase)
 */
const PROVIDER_ICON_MAP: Record<string, string> = {
  // Core providers
  'openai': '/llm-icons/gpt.png',
  'anthropic': '/llm-icons/claude.png',
  'google': '/llm-icons/gemini.png',
  'perplexity': '/llm-icons/perplexity.png',
  
  // All other providers
  'alibaba': '/llm-icons/alibaba.png',
  'allenai': '/llm-icons/allenai.png',
  'arcee-ai': '/llm-icons/arcee.png',
  'baidu': '/llm-icons/baidu.png',
  'bytedance-seed': '/llm-icons/bytedanceseed.png',
  'cohere': '/llm-icons/cohere.png',
  'deepcogito': '/llm-icons/deepcogito.png',
  'deepseek': '/llm-icons/deepseek.png',
  'essentialai': '/llm-icons/essentialai.png',
  'gryphe': '/llm-icons/gryphe.png',
  'inception': '/llm-icons/inception.png',
  'kwaipilot': '/llm-icons/kwaipilot.png',
  'meta-llama': '/llm-icons/Meta.png',
  'microsoft': '/llm-icons/Microsoft.png',
  'minimax': '/llm-icons/minimax.png',
  'mistralai': '/llm-icons/mistral.png',
  'moonshotai': '/llm-icons/moonshot.png',
  'nex-agi': '/llm-icons/nexagi.png',
  'nousresearch': '/llm-icons/nousresearch.png',
  'nvidia': '/llm-icons/nvidia.png',
  'opengvlab': '/llm-icons/opengvlab.png',
  'prime-intellect': '/llm-icons/prime-intelect.png',
  'qwen': '/llm-icons/qwen.png',
  'stepfun-ai': '/llm-icons/stepfun.png',
  'tencent': '/llm-icons/tencent.png',
  'venice': '/llm-icons/venice.png',
  'x-ai': '/llm-icons/x-aI.png',
  'z-ai': '/llm-icons/Z-ai.png',
  // Providers without icons get placeholder (neversleep, sao10k, thedrummer, tngtech, undi95)
};

/**
 * The neutral model mark shown whenever a member's model identity is unknown —
 * unresolvable ids, providers without icons, and loading states.
 */
export const PLACEHOLDER_ICON = '/llm-icons/ai-placeholder.png';

/**
 * Get the icon path for a provider string
 * @param provider - Provider name string exactly as returned from API
 * @returns Icon path string
 */
export function getProviderIconPath(provider: string): string {
  if (!provider) return PLACEHOLDER_ICON;
  return PROVIDER_ICON_MAP[provider] || PLACEHOLDER_ICON;
}

/**
 * Get icon paths for multiple providers
 * @param providers - Array of provider name strings
 * @returns Array of unique icon paths
 */
export function getProviderIconPaths(providers: string[]): string[] {
  if (!providers || !Array.isArray(providers)) return [];
  
  const seen = new Set<string>();
  const iconPaths: string[] = [];
  
  for (const provider of providers) {
    const iconPath = getProviderIconPath(provider);
    if (!seen.has(iconPath)) {
      seen.add(iconPath);
      iconPaths.push(iconPath);
    }
  }
  
  return iconPaths;
}
